/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';

import { ColumnTexts } from '../../enums';
import { TimeUnitNames, TimeUnitsInMs, TimeUnitsInSeconds, isNumber, makeTwoDigit } from '../../core';
import { plural } from '../string-helpers';
import { DurationFormatTypes } from './enums';

import type { FullWordParsedTime, TimeHelpersTexts } from './interfaces';

dayjs.extend(duration);

const parseTime = (seconds: number, texts: TimeHelpersTexts): FullWordParsedTime[] => {
    const {
        second: secondText = TimeUnitNames.Second,
        minute: minuteText = TimeUnitNames.Minute,
        hour: hourText = TimeUnitNames.Hour,
    } = texts;

    if (seconds >= TimeUnitsInSeconds.Hour) {
        const hours = Math.floor(seconds / TimeUnitsInSeconds.Hour);

        return [
            {
                value: hours,
                text: plural(hours, hourText),
            },
        ]
            .concat(parseTime(seconds - (hours * TimeUnitsInSeconds.Hour), texts));
    } else if (seconds >= TimeUnitsInSeconds.Minute) {
        const minutes = Math.floor(seconds / TimeUnitsInSeconds.Minute);

        return [
            {
                value: minutes,
                text: plural(minutes, minuteText),
            },
        ]
            .concat(parseTime(seconds - (minutes * TimeUnitsInSeconds.Minute), texts));
    }
    return [
        {
            value: seconds,
            text: plural(seconds, secondText),
        },
    ];
};

const formatDurationToFullWord = (valueInSeconds: number, texts: TimeHelpersTexts, precision = 2): string => {
    if (valueInSeconds === -1) {
        return ColumnTexts.DashSymbol;
    }

    const items: FullWordParsedTime[] = parseTime(valueInSeconds, texts);
    let result = '';

    for (let i = 0; i < items.length && precision > 0; i++) {
        const { value, text } = items[i];

        if ((value !== 0 && items.length > 1) || items.length === 1) {
            result += `${text} `;
            precision -= 1;
        }
    }

    return result.trim();
};

const formatDurationToTIMEw4 = (valueInSeconds: number): string => {
    const valueInMs = valueInSeconds * TimeUnitsInMs.Second;
    const hours = Math.floor(dayjs.duration(valueInMs).asHours());
    const minutes = Math.floor(dayjs.duration(valueInMs).minutes());

    return `${makeTwoDigit(hours)}:${makeTwoDigit(minutes)}`;
};

const formatDurationToTIMEw8 = (valueInSeconds: number): string => {
    const valueInMs = valueInSeconds * TimeUnitsInMs.Second;
    const hours = Math.floor(dayjs.duration(valueInMs).asHours());
    const minutes = Math.floor(dayjs.duration(valueInMs).minutes());
    const seconds = Math.floor(dayjs.duration(valueInMs).seconds());

    return `${makeTwoDigit(hours)}:${makeTwoDigit(minutes)}:${makeTwoDigit(seconds)}`;
};

const formatDurationToMTIMEw = (valueInSeconds: number): string => {
    const valueInMs = valueInSeconds * TimeUnitsInMs.Second;
    const minutes = Math.floor(dayjs.duration(valueInMs).asMinutes());
    const seconds = Math.floor(dayjs.duration(valueInMs).seconds());

    return `${makeTwoDigit(minutes)}:${makeTwoDigit(seconds)}`;
};

const formatDurationToString = (value: number, formatType: DurationFormatTypes, texts: TimeHelpersTexts = {}): string => {
    if (!isNumber(value)) {
        return ColumnTexts.DashSymbol;
    }

    switch (formatType) {
        case (DurationFormatTypes.FullWord): return formatDurationToFullWord(value, texts, 3);
        case (DurationFormatTypes.ShortFullWord): return formatDurationToFullWord(value, texts);
        case (DurationFormatTypes.TIMEw4): return formatDurationToTIMEw4(value);
        case (DurationFormatTypes.TIMEw8): return formatDurationToTIMEw8(value);
        case (DurationFormatTypes.MTIMEw): return formatDurationToMTIMEw(value);
        default: return formatDurationToFullWord(value, texts);
    }
};

export {
    formatDurationToString,
};
